'use client';

import { useEffect } from 'react';

interface ThemeSettings {
    primary?: string;
    secondary?: string;
    accent?: string;
    card?: string;
    miniPlayer?: string;
    publicAppBackgroundStart?: string;
    publicAppBackgroundMiddle?: string;
    publicAppBackgroundEnd?: string;
}

const hexToHsl = (hex: string): string | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return null;

    let r = parseInt(result[1], 16);
    let g = parseInt(result[2], 16);
    let b = parseInt(result[3], 16);

    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export function ThemeSync() {
    useEffect(() => {
        const fetchThemeParams = async () => {
            try {
                const response = await fetch('/api/settings/visual_appearance');
                if (response.ok) {
                    const data = await response.json();
                    if (data.value) {
                        applyTheme(data.value);
                    }
                }
            } catch (error) {
                console.error('Failed to load theme settings:', error);
            }
        };

        fetchThemeParams();
    }, []);

    const applyTheme = (colors: ThemeSettings) => {
        const root = document.documentElement;
        if (colors.primary) root.style.setProperty('--primary', colors.primary);
        if (colors.secondary) root.style.setProperty('--secondary', colors.secondary);
        if (colors.accent) root.style.setProperty('--accent', colors.accent);
        if (colors.card) {
            const hsl = hexToHsl(colors.card);
            if (hsl) root.style.setProperty('--card', hsl);
        }
        if (colors.miniPlayer) {
            root.style.setProperty('--mini-player', colors.miniPlayer);
        }
        if (colors.publicAppBackgroundStart) {
            root.style.setProperty('--public-app-background-start', colors.publicAppBackgroundStart);
        }
        if (colors.publicAppBackgroundMiddle) {
            root.style.setProperty('--public-app-background-middle', colors.publicAppBackgroundMiddle);
        }
        if (colors.publicAppBackgroundEnd) {
            root.style.setProperty('--public-app-background-end', colors.publicAppBackgroundEnd);
        }
    };

    return null;
}
